<template>
    <div class="isi-tuner-container" v-if="isAuthorized">
        <!-- Toast Notification for Shortcuts / Hide Action -->
        <transition name="toast-fade">
            <div class="isi-tuner-toast" v-if="toastVisible" @click="toastVisible = false">
                {{ toastMessage }}
            </div>
        </transition>

        <!-- Floating Toggle Trigger Button -->
        <button v-show="isTunerVisible" class="isi-tuner-toggle" @click="isOpen = !isOpen" title="Open Settings & Tuner (Press Alt+H to hide)">
            <span class="icon">⚙️</span>
            <span class="label">Settings</span>
            <span class="badge" v-if="activeTab === 'isi' && activePreset !== 'custom'">{{ presetName }}</span>
            <span class="badge env-badge" v-else-if="activeTab === 'env'">HCP Pro</span>
        </button>

        <!-- Floating Settings Drawer / Control Panel -->
        <transition name="tuner-slide">
            <div class="isi-tuner-panel" v-if="isOpen && isTunerVisible">
                <!-- Panel Header with Tabs -->
                <div class="tuner-header">
                    <div class="d-flex align-items-center justify-content-between w-100 mb-2">
                        <div class="d-flex align-items-center">
                            <span class="tuner-logo mr-2">🛠️</span>
                            <div>
                                <h6 class="m-0 font-weight-bold">Platform Settings</h6>
                                <small class="text-muted">HCP Interactive design & environment controls</small>
                            </div>
                        </div>
                        <button class="tuner-close-btn" @click="isOpen = false" title="Close Settings">&times;</button>
                    </div>

                    <!-- Tab Switcher -->
                    <div class="settings-tabs">
                        <button 
                            class="tab-btn" 
                            :class="{ active: activeTab === 'isi' }" 
                            @click="activeTab = 'isi'">
                            🎨 ISI Tuner
                        </button>
                        <button 
                            class="tab-btn" 
                            :class="{ active: activeTab === 'release' }" 
                            @click="activeTab = 'release'">
                            🚀 Site Release
                        </button>
                        <button 
                            class="tab-btn" 
                            :class="{ active: activeTab === 'env' }" 
                            @click="activeTab = 'env'">
                            ℹ️ Platform Info
                        </button>
                    </div>
                </div>

                <!-- Panel Body: Tab 1 (ISI Tuner) -->
                <div class="tuner-body" v-show="activeTab === 'isi'">
                    <!-- Presets Selection -->
                    <div class="tuner-section">
                        <label class="section-label">Design Presets</label>
                        <div class="preset-buttons">
                            <button 
                                :class="{ active: activePreset === 'opt1' }" 
                                @click="applyPreset('opt1')">
                                <b>Recommended</b>
                                <span>Option 1 (100% Full)</span>
                            </button>
                            <button 
                                :class="{ active: activePreset === 'opt2' }" 
                                @click="applyPreset('opt2')">
                                <b>Option 2</b>
                                <span>Compact Height</span>
                            </button>
                            <button 
                                :class="{ active: activePreset === 'opt3' }" 
                                @click="applyPreset('opt3')">
                                <b>Option 3</b>
                                <span>Floating Card</span>
                            </button>
                            <button 
                                :class="{ active: activePreset === 'baseline' }" 
                                @click="applyPreset('baseline')">
                                <b>Baseline</b>
                                <span>Original (100% Full)</span>
                            </button>
                        </div>
                    </div>

                    <!-- Custom Sliders -->
                    <div class="tuner-section">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <label class="section-label m-0">Max Width</label>
                            <div class="custom-control custom-switch">
                                <input type="checkbox" class="custom-control-input" id="fullWidthToggleHcp" v-model="isFullWidth" @change="onFullWidthChange">
                                <label class="custom-control-label small" for="fullWidthToggleHcp">100% Full</label>
                            </div>
                        </div>
                        <div class="slider-row" v-if="!isFullWidth">
                            <input type="range" min="600" max="1600" step="20" v-model.number="config.maxWidth" @input="onCustomChange">
                            <span class="slider-val">{{ config.maxWidth }}px</span>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Baseline Height (Pre-Scroll)</label>
                        <div class="slider-row">
                            <input type="range" min="100" max="500" step="5" v-model.number="config.baselineHeight" @input="onCustomChange('baselineHeight')">
                            <span class="slider-val">{{ config.baselineHeight }}px</span>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Collapsed Height (On-Scroll)</label>
                        <div class="slider-row">
                            <input type="range" min="80" max="400" step="5" v-model.number="config.collapsedHeight" @input="onCustomChange('collapsedHeight')">
                            <span class="slider-val">{{ config.collapsedHeight }}px</span>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Text Scroll Area Height</label>
                        <div class="slider-row">
                            <input type="range" min="40" max="350" step="5" v-model.number="config.textHeight" @input="onCustomChange('textHeight')">
                            <span class="slider-val">{{ config.textHeight }}px</span>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Bottom Float Margin</label>
                        <div class="slider-row">
                            <input type="range" min="0" max="40" step="2" v-model.number="config.bottomOffset" @input="onCustomChange">
                            <span class="slider-val">{{ config.bottomOffset }}px</span>
                        </div>
                    </div>

                    <div class="tuner-row">
                        <div class="tuner-col">
                            <label class="section-label">Top Corner Radius</label>
                            <div class="slider-row">
                                <input type="range" min="0" max="28" step="2" v-model.number="config.topRadius" @input="onCustomChange">
                                <span class="slider-val">{{ config.topRadius }}px</span>
                            </div>
                        </div>
                        <div class="tuner-col">
                            <label class="section-label">Bottom Corner Radius</label>
                            <div class="slider-row">
                                <input type="range" min="0" max="28" step="2" v-model.number="config.bottomRadius" @input="onCustomChange">
                                <span class="slider-val">{{ config.bottomRadius }}px</span>
                            </div>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Elevation / Shadow</label>
                        <select class="form-control form-control-sm tuner-select" v-model="config.shadow" @change="onCustomChange">
                            <option value="none">None (Flat)</option>
                            <option value="subtle">Subtle Elevation</option>
                            <option value="medium">Medium Depth</option>
                            <option value="elevated">Elevated 3D Card (Floating)</option>
                        </select>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Border Outline</label>
                        <select class="form-control form-control-sm tuner-select" v-model="config.border" @change="onCustomChange">
                            <option value="none">None</option>
                            <option value="subtle">Subtle Outline (1px rgba 8%)</option>
                            <option value="distinct">Distinct Border (1px solid #d1d5db)</option>
                        </select>
                    </div>

                    <!-- Scroll-Vanish Offset (OPDP / Fair Balance Gap Fix) -->
                    <div class="tuner-section vanish-section">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <label class="section-label m-0">Scroll-Vanish Offset</label>
                            <span class="vanish-badge">OPDP Fair Balance Fix</span>
                        </div>
                        <small class="vanish-help">Pixels static ISI must enter viewport before tray hides (eliminates CTA visibility gap)</small>
                        <div class="slider-row mt-1">
                            <input type="range" min="0" max="500" step="25" v-model.number="config.vanishOffset" @input="onCustomChange">
                            <span class="slider-val">{{ config.vanishOffset }}px</span>
                        </div>
                    </div>
                </div>

                <!-- Panel Body: Tab 2 (Site Release / Oct 1 Preview) -->
                <div class="tuner-body" v-show="activeTab === 'release'">
                    <!-- Cutover Status Card -->
                    <div class="platform-info-card">
                        <div class="d-flex align-items-center justify-content-between mb-1">
                            <span class="font-weight-bold" style="font-size: 13px; color: #0284c7;">October 1, 2026 Cutover</span>
                            <span class="badge" :class="isOct1Active ? 'badge-info' : 'badge-secondary'">
                                {{ isOct1Active ? 'Post-Oct 1 Active' : 'Pre-Oct 1 Active' }}
                            </span>
                        </div>
                        <p class="small text-muted mb-2">
                            Automatic cutover is scheduled for <b>October 1, 2026</b>. Use this panel to preview either state instantly.
                        </p>
                        <div class="small">
                            <div>⏰ <b>Countdown:</b> {{ cutoverStatusText }}</div>
                            <div>🎯 <b>Active State:</b> {{ isOct1Active ? 'October ADHD Awareness Campaign' : 'Standard Baseline' }}</div>
                        </div>
                    </div>

                    <!-- Release Mode Selection -->
                    <div class="tuner-section">
                        <label class="section-label">Select Active Mode (Preview)</label>
                        <div class="d-flex flex-column" style="gap: 8px;">
                            <!-- Option 1: Auto -->
                            <button 
                                class="btn btn-sm text-left d-flex align-items-center justify-content-between"
                                :class="selectedReleaseMode === 'auto' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setMode('auto')">
                                <div>
                                    <b>🔄 Auto (Date-Based)</b>
                                    <div class="small" style="opacity: 0.85;">Switches at midnight Oct 1, 2026</div>
                                </div>
                                <span class="badge badge-light" v-if="selectedReleaseMode === 'auto'">Active</span>
                            </button>

                            <!-- Option 2: Pre-Oct 1 -->
                            <button 
                                class="btn btn-sm text-left d-flex align-items-center justify-content-between"
                                :class="selectedReleaseMode === 'pre' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setMode('pre')">
                                <div>
                                    <b>📅 Pre-Oct 1 (Current Site)</b>
                                    <div class="small" style="opacity: 0.85;">Standard baseline without October interstitial</div>
                                </div>
                                <span class="badge badge-light" v-if="selectedReleaseMode === 'pre'">Active</span>
                            </button>

                            <!-- Option 3: Post-Oct 1 -->
                            <button 
                                class="btn btn-sm text-left d-flex align-items-center justify-content-between"
                                :class="selectedReleaseMode === 'post' ? 'btn-primary' : 'btn-outline-secondary'"
                                @click="setMode('post')">
                                <div>
                                    <b>🚀 Post-Oct 1 (October Campaign)</b>
                                    <div class="small" style="opacity: 0.85;">Enables October ADHD Awareness Interstitial</div>
                                </div>
                                <span class="badge badge-light" v-if="selectedReleaseMode === 'post'">Active</span>
                            </button>
                        </div>
                    </div>

                    <!-- Popup Preview Actions -->
                    <div class="tuner-section">
                        <label class="section-label">First Visit & Page Previews</label>
                        <div class="d-flex flex-column" style="gap: 6px;">
                            <button type="button" @click="testFirstVisitExperience" class="btn btn-sm btn-success text-left font-weight-bold d-flex align-items-center justify-content-between">
                                <span>🌟 Test First Visit Experience</span>
                                <span class="badge badge-light" style="color: #166534;">Reset & Test</span>
                            </button>
                            <small class="text-muted" style="font-size: 11px; line-height: 1.35;">
                                Resets visitor flags so the <b>HCP Certification Gate</b> ("Are you a healthcare professional?") displays first on the homepage, followed by the October ADHD popup on acceptance. Keeps your selected release mode. Drawer closes automatically (press <b>Alt+H</b> to reopen).
                            </small>
                            <button type="button" @click="triggerOctPopup" class="btn btn-sm btn-outline-primary text-left mt-1">
                                📢 Preview October ADHD Awareness Popup
                            </button>
                            <router-link to="/dosing-administration" class="btn btn-sm btn-outline-secondary text-left">
                                💊 Dosing & Administration
                            </router-link>
                            <router-link to="/clinical-profile" class="btn btn-sm btn-outline-secondary text-left">
                                📊 Clinical Profile
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- Panel Body: Tab 3 (Platform & Environment Info) -->
                <div class="tuner-body" v-show="activeTab === 'env'">
                    <div class="platform-info-card">
                        <div class="font-weight-bold mb-1" style="font-size: 13px; color: #0284c7;">
                            🏥 HCP Platform Status
                        </div>
                        <div class="platform-meta-item">
                            <span class="meta-label">Environment:</span>
                            <span class="meta-val font-weight-bold">{{ isProd ? 'Production' : 'Dev / Staging' }}</span>
                        </div>
                        <div class="platform-meta-item">
                            <span class="meta-label">Backend API:</span>
                            <span class="meta-val badge badge-success">.NET 10 Isolated Worker</span>
                        </div>
                        <div class="platform-meta-item">
                            <span class="meta-label">App Insights:</span>
                            <span class="meta-val badge badge-info">Active & Verified</span>
                        </div>
                        <div class="platform-meta-item">
                            <span class="meta-label">GTM Container:</span>
                            <span class="meta-val font-family-monospace">GTM-KD5HRBK</span>
                        </div>
                        <div class="platform-meta-item">
                            <span class="meta-label">Canonical Tags:</span>
                            <span class="meta-val badge badge-success">12 Routes Active</span>
                        </div>
                    </div>

                    <div class="tuner-section">
                        <label class="section-label">Quick Links</label>
                        <div class="d-flex flex-column" style="gap: 6px;">
                            <router-link to="/" class="btn btn-sm btn-outline-secondary text-left">
                                🏠 HCP Homepage
                            </router-link>
                            <router-link to="/dosing-administration" class="btn btn-sm btn-outline-secondary text-left">
                                💊 Dosing & Administration
                            </router-link>
                            <router-link to="/clinical-profile" class="btn btn-sm btn-outline-secondary text-left">
                                📊 Clinical Profile
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- Panel Footer / Actions -->
                <div class="tuner-footer">
                    <button class="btn btn-sm btn-outline-info" @click="toggleTunerVisibility" title="Hide settings for screenshots (Alt+H)">
                        📷 Hide (Alt+H)
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="copyCss" :title="copiedCss ? 'Copied!' : 'Copy CSS rules'" v-if="activeTab === 'isi'">
                        <span v-if="copiedCss">✓ Copied!</span>
                        <span v-else>📋 CSS</span>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="copyShareLink" :title="copiedLink ? 'Copied link!' : 'Copy share link'">
                        <span v-if="copiedLink">✓ Link!</span>
                        <span v-else>🔗 Link</span>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="resetToBaseline">Reset</button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { 
    releaseState, 
    setReleaseMode, 
    initReleaseManager, 
    isSettingsAuthorized,
    getCutoverStatus,
    releaseBus 
} from '../../utils/releaseManager';

export default {
    name: "Settings",
    data() {
        return {
            isOpen: false,
            isTunerVisible: false,
            toastVisible: false,
            toastMessage: '',
            toastTimer: null,
            activeTab: 'isi', // 'isi' | 'release' | 'env'
            activePreset: 'opt1',
            isFullWidth: true,
            copiedCss: false,
            copiedLink: false,
            isProd: false,
            isAuthorized: true,
            config: {
                maxWidth: 1140,
                baselineHeight: 240,
                collapsedHeight: 240,
                textHeight: 195,
                bottomOffset: 0,
                topRadius: 12,
                bottomRadius: 0,
                shadow: 'none',
                border: 'none',
                isCentered: false,
                vanishOffset: 250
            }
        };
    },
    computed: {
        presetName() {
            switch (this.activePreset) {
                case 'opt1': return 'Recommended';
                case 'opt2': return 'Option 2';
                case 'opt3': return 'Option 3';
                case 'baseline': return 'Baseline';
                default: return 'Custom';
            }
        },
        selectedReleaseMode() {
            return releaseState.mode;
        },
        isOct1Active() {
            return releaseState.isOct1Active;
        },
        cutoverStatusText() {
            const status = getCutoverStatus();
            if (status.isPastCutover) {
                return `Cutover passed (${status.daysSince} days ago)`;
            }
            return `${status.daysRemaining} days until Oct 1, 2026 cutover`;
        }
    },
    created() {
        initReleaseManager();
        if (typeof window !== 'undefined') {
            const host = window.location.hostname.toLowerCase();
            this.isProd = host === 'www.jornaypm-pro.com' || host === 'jornaypm-pro.com';
            this.isAuthorized = isSettingsAuthorized();
        }
    },
    mounted() {
        if (!this.isAuthorized) {
            return;
        }

        // Read URL query params via both URLSearchParams and Vue route
        const searchParams = new URLSearchParams(window.location.search);
        const isiParam = searchParams.get('isi') || (this.$route && this.$route.query && this.$route.query.isi);
        const settingsParam = searchParams.get('settings') || searchParams.get('tuner') || searchParams.get('showtuner') || 
                              (this.$route && this.$route.query && (this.$route.query.settings || this.$route.query.tuner || this.$route.query.showtuner));
        const cleanParam = searchParams.get('clean') || searchParams.get('mrc') || searchParams.get('hide') || searchParams.get('notuner');
        const tabParam = searchParams.get('tab') || (this.$route && this.$route.query && this.$route.query.tab);

        if (tabParam === 'release' || tabParam === 'oct1') {
            this.activeTab = 'release';
        } else if (tabParam === 'env' || tabParam === 'info') {
            this.activeTab = 'env';
        }

        // Tuner visibility: hidden by default unless ?settings=1 or ?tuner=1
        if (settingsParam === '1' && !cleanParam) {
            this.isTunerVisible = true;
        } else {
            this.isTunerVisible = false;
        }

        // Active preset: Recommended (opt1) by default unless ?isi=2/3/baseline/custom
        if (isiParam === '1' || isiParam === 'recommended') {
            this.applyPreset('opt1');
        } else if (isiParam === '2') {
            this.applyPreset('opt2');
        } else if (isiParam === '3') {
            this.applyPreset('opt3');
        } else if (isiParam === 'baseline') {
            this.applyPreset('baseline');
        } else if (isiParam === 'custom') {
            this.loadCustomFromQuery();
        } else {
            this.applyPreset('opt1');
        }

        // Show brief initial toast informing user of Alt+H activation shortcut (only in non-prod)
        if (!this.isProd && !this.isTunerVisible && searchParams.get('notoast') !== '1') {
            setTimeout(() => {
                this.showToast('⚙️ Settings hidden — Press Alt+H to show');
            }, 600);
        }

        window.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
        if (this.toastTimer) clearTimeout(this.toastTimer);
    },
    methods: {
        applyPreset(preset) {
            this.activePreset = preset;
            if (preset === 'opt1') {
                // Option 1 (Recommended): 100% Full Width, 240px Baseline Height, 240px Collapsed Height, 195px Inner Text, 12px Top Radius, No Shadow, No Border, 250px Vanish Offset
                this.isFullWidth = true;
                this.config = {
                    maxWidth: 1140,
                    baselineHeight: 240,
                    collapsedHeight: 240,
                    textHeight: 195,
                    bottomOffset: 0,
                    topRadius: 12,
                    bottomRadius: 0,
                    shadow: 'none',
                    border: 'none',
                    isCentered: false,
                    vanishOffset: 250
                };
            } else if (preset === 'opt2') {
                // Option 2: Compact Height
                this.isFullWidth = true;
                this.config = {
                    maxWidth: 1140,
                    baselineHeight: 200,
                    collapsedHeight: 135,
                    textHeight: 85,
                    bottomOffset: 0,
                    topRadius: 0,
                    bottomRadius: 0,
                    shadow: 'none',
                    border: 'none',
                    isCentered: false,
                    vanishOffset: 250
                };
            } else if (preset === 'opt3') {
                // Option 3: Floating Rounded Card
                this.isFullWidth = false;
                this.config = {
                    maxWidth: 1080,
                    baselineHeight: 280,
                    collapsedHeight: 200,
                    textHeight: 145,
                    bottomOffset: 16,
                    topRadius: 16,
                    bottomRadius: 16,
                    shadow: 'elevated',
                    border: 'subtle',
                    isCentered: true,
                    vanishOffset: 300
                };
            } else if (preset === 'baseline') {
                // Baseline: Original Full Width
                this.isFullWidth = true;
                this.config = {
                    maxWidth: 1140,
                    baselineHeight: 280,
                    collapsedHeight: 140,
                    textHeight: 90,
                    bottomOffset: 0,
                    topRadius: 0,
                    bottomRadius: 0,
                    shadow: 'none',
                    border: 'none',
                    isCentered: false,
                    vanishOffset: 0
                };
            }
            this.applyStylesToDom();
        },
        onFullWidthChange() {
            this.activePreset = 'custom';
            this.applyStylesToDom();
        },
        onCustomChange(field) {
            this.activePreset = 'custom';
            if (field === 'collapsedHeight') {
                this.config.textHeight = Math.max(40, this.config.collapsedHeight - 45);
            }
            this.applyStylesToDom();
        },
        applyStylesToDom() {
            const root = document.documentElement;
            
            // Width
            const widthVal = this.isFullWidth ? '100%' : (this.config.maxWidth + 'px');
            root.style.setProperty('--isi-tuner-max-width', widthVal);
            root.style.setProperty('--isi-tuner-width', this.isFullWidth ? '100%' : '92%');
            root.style.setProperty('--isi-tuner-left', this.isFullWidth ? '0' : '50%');
            root.style.setProperty('--isi-tuner-transform', this.isFullWidth ? 'none' : 'translateX(-50%)');

            // Heights
            root.style.setProperty('--isi-baseline-height', this.config.baselineHeight + 'px');
            root.style.setProperty('--isi-baseline-inner-height', Math.max(40, this.config.baselineHeight - 45) + 'px');
            root.style.setProperty('--isi-collapsed-height', this.config.collapsedHeight + 'px');
            root.style.setProperty('--isi-tuner-height', this.config.collapsedHeight + 'px');
            root.style.setProperty('--isi-tuner-inner-height', this.config.textHeight + 'px');

            // Bottom offset & Radius
            root.style.setProperty('--isi-tuner-bottom', this.config.bottomOffset + 'px');
            root.style.setProperty('--isi-tuner-top-radius', this.config.topRadius + 'px');
            root.style.setProperty('--isi-tuner-bottom-radius', this.config.bottomRadius + 'px');

            // Shadow
            let shadowVal = 'none';
            if (this.config.shadow === 'subtle') shadowVal = '0 -4px 12px rgba(0, 0, 0, 0.08)';
            if (this.config.shadow === 'medium') shadowVal = '0 -6px 24px rgba(0, 0, 0, 0.18)';
            if (this.config.shadow === 'elevated') shadowVal = '0 8px 32px rgba(0, 0, 0, 0.22)';
            root.style.setProperty('--isi-tuner-shadow', shadowVal);

            // Border
            let borderVal = 'none';
            if (this.config.border === 'subtle') borderVal = '1px solid rgba(0, 0, 0, 0.09)';
            if (this.config.border === 'distinct') borderVal = '1px solid #cbd5e1';
            root.style.setProperty('--isi-tuner-border', borderVal);

            // Scroll-Vanish Offset (Fair Balance Gap Fix)
            root.style.setProperty('--isi-tuner-vanish-offset', (this.config.vanishOffset || 0) + 'px');

            // Show scrollable text area for options
            root.style.setProperty('--isi-tuner-show-text', 'block');

            // Dispatch scroll event to force recalculation immediately
            window.dispatchEvent(new Event('scroll'));
        },
        copyCss() {
            const css = `/* JORNAY PM PRO - ISI Resized Configuration */
:root {
  --isi-tuner-max-width: ${this.isFullWidth ? '100%' : this.config.maxWidth + 'px'};
  --isi-tuner-width: ${this.isFullWidth ? '100%' : '92%'};
  --isi-baseline-height: ${this.config.baselineHeight}px;
  --isi-baseline-inner-height: ${Math.max(40, this.config.baselineHeight - 45)}px;
  --isi-collapsed-height: ${this.config.collapsedHeight}px;
  --isi-tuner-height: ${this.config.collapsedHeight}px;
  --isi-tuner-inner-height: ${this.config.textHeight}px;
  --isi-tuner-bottom: ${this.config.bottomOffset}px;
  --isi-tuner-top-radius: ${this.config.topRadius}px;
  --isi-tuner-bottom-radius: ${this.config.bottomRadius}px;
  --isi-tuner-shadow: ${this.config.shadow};
  --isi-tuner-border: ${this.config.border};
  --isi-tuner-vanish-offset: ${this.config.vanishOffset}px;
}`;
            navigator.clipboard.writeText(css);
            this.copiedCss = true;
            setTimeout(() => { this.copiedCss = false; }, 2500);
        },
        copyShareLink() {
            const url = new URL(window.location.href);
            url.searchParams.set('tab', this.activeTab);
            if (this.activePreset === 'opt1') url.searchParams.set('isi', '1');
            else if (this.activePreset === 'opt2') url.searchParams.set('isi', '2');
            else if (this.activePreset === 'opt3') url.searchParams.set('isi', '3');
            else if (this.activePreset === 'baseline') url.searchParams.set('isi', 'baseline');
            else {
                url.searchParams.set('isi', 'custom');
                url.searchParams.set('w', this.isFullWidth ? 'full' : this.config.maxWidth);
                url.searchParams.set('bh', this.config.baselineHeight);
                url.searchParams.set('h', this.config.collapsedHeight);
                url.searchParams.set('th', this.config.textHeight);
                url.searchParams.set('b', this.config.bottomOffset);
                url.searchParams.set('tr', this.config.topRadius);
                url.searchParams.set('br', this.config.bottomRadius);
                url.searchParams.set('sh', this.config.shadow);
                url.searchParams.set('vo', this.config.vanishOffset);
            }
            navigator.clipboard.writeText(url.toString());
            this.copiedLink = true;
            setTimeout(() => { this.copiedLink = false; }, 2500);
        },
        resetToBaseline() {
            this.applyPreset('opt1');
        },
        loadCustomFromQuery() {
            const q = this.$route.query;
            this.activePreset = 'custom';
            this.isFullWidth = q.w === 'full';
            if (!this.isFullWidth && q.w) this.config.maxWidth = parseInt(q.w, 10);
            if (q.bh) this.config.baselineHeight = parseInt(q.bh, 10);
            if (q.h) this.config.collapsedHeight = parseInt(q.h, 10);
            if (q.th) this.config.textHeight = parseInt(q.th, 10);
            if (q.b) this.config.bottomOffset = parseInt(q.b, 10);
            if (q.tr) this.config.topRadius = parseInt(q.tr, 10);
            if (q.br) this.config.bottomRadius = parseInt(q.br, 10);
            if (q.sh) this.config.shadow = q.sh;
            if (q.vo) this.config.vanishOffset = parseInt(q.vo, 10);
            this.applyStylesToDom();
        },
        handleKeyDown(e) {
            if (e.altKey && (e.key === 'h' || e.key === 'H')) {
                e.preventDefault();
                this.toggleTunerVisibility();
            }
        },
        toggleTunerVisibility() {
            this.isTunerVisible = !this.isTunerVisible;
            if (!this.isTunerVisible) {
                this.isOpen = false;
                this.showToast('📷 Settings hidden (Press Alt+H to restore)');
            } else {
                this.showToast('⚙️ Settings visible');
            }
        },
        showToast(msg) {
            this.toastMessage = msg;
            this.toastVisible = true;
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
                this.toastVisible = false;
            }, 3000);
        },
        setMode(mode) {
            setReleaseMode(mode, true);
            this.showToast(`🚀 Release mode switched to: ${mode.toUpperCase()}`);
        },
        testFirstVisitExperience() {
            // 1. Preserve active release date mode and tuner preset
            const savedReleaseMode = releaseState.mode || localStorage.getItem('jornay_release_mode') || 'auto';
            const savedPreset = this.activePreset || 'opt1';
            const savedConfig = JSON.stringify(this.config);

            // 2. Clear all visitor session and localStorage seen flags
            try {
                sessionStorage.clear();
                localStorage.removeItem('journayhcpmodalacepted');
                localStorage.removeItem('journayhcpcookieacepted');
                localStorage.removeItem('jny_hcp_adhd_oct_seen');
                localStorage.removeItem('shortage_v');
            } catch (e) {
                console.warn('Error clearing visitor storage:', e);
            }

            // 3. Re-persist the user's chosen preview settings
            try {
                localStorage.setItem('jornay_release_mode', savedReleaseMode);
                localStorage.setItem('jny_isi_preset', savedPreset);
                localStorage.setItem('jny_isi_config', savedConfig);
            } catch (e) {
                console.warn('Error persisting preview settings:', e);
            }

            // 4. Hide settings drawer & tuner overlay for clean production preview
            this.isOpen = false;
            this.isTunerVisible = false;

            // 5. Navigate to root homepage with clean reload
            window.location.href = '/';
        },
        triggerOctPopup() {
            releaseBus.$emit('trigger-oct-popup');
            this.showToast('📢 Displaying October ADHD Awareness Popup');
        }
    }
};
</script>

<style lang="scss" scoped>
@media print {
    .isi-tuner-container {
        display: none !important;
    }
}

.isi-tuner-toast {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: rgba(17, 24, 39, 0.92);
    color: #ffffff;
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    z-index: 99999999;
    cursor: pointer;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
}

.toast-fade-enter-active, .toast-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.toast-fade-enter, .toast-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.isi-tuner-toggle {
    position: fixed;
    bottom: 30px;
    right: 25px;
    z-index: 99999998;
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    color: #ffffff;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(2, 132, 199, 0.4);
    transition: all 0.25s ease;
    font-family: inherit;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(2, 132, 199, 0.55);
        background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
    }

    .icon {
        font-size: 16px;
        line-height: 1;
    }

    .label {
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.3px;
    }

    .badge {
        background: rgba(255, 255, 255, 0.22);
        font-size: 11px;
        padding: 2px 7px;
        border-radius: 12px;
        font-weight: 600;

        &.env-badge {
            background: #0ea5e9;
            color: #ffffff;
        }
    }
}

.isi-tuner-panel {
    position: fixed;
    bottom: 85px;
    right: 25px;
    width: 360px;
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 110px);
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08);
    z-index: 99999998;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #1e293b;
}

.tuner-slide-enter-active, .tuner-slide-leave-active {
    transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}
.tuner-slide-enter, .tuner-slide-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
}

.tuner-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 16px 8px;

    .tuner-logo {
        font-size: 18px;
    }

    .tuner-close-btn {
        background: transparent;
        border: none;
        font-size: 20px;
        color: #64748b;
        cursor: pointer;
        line-height: 1;
        padding: 0 4px;
        border-radius: 4px;

        &:hover {
            color: #0f172a;
            background: #e2e8f0;
        }
    }
}

.settings-tabs {
    display: flex;
    gap: 6px;
    background: #e2e8f0;
    padding: 3px;
    border-radius: 8px;

    .tab-btn {
        flex: 1;
        background: transparent;
        border: none;
        padding: 6px 10px;
        font-size: 12px;
        font-weight: 700;
        color: #64748b;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: all 0.15s ease;

        &.active {
            background: #ffffff;
            color: #0369a1;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
    }
}

.tuner-body {
    padding: 14px 16px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tuner-section {
    display: flex;
    flex-direction: column;

    .section-label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 700;
        color: #475569;
        margin-bottom: 6px;
    }
}

.platform-info-card {
    background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
    border: 1px solid #bae6fd;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .platform-meta-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 11.5px;
        padding: 2px 0;
        border-bottom: 1px solid #e0f2fe;

        &:last-child {
            border-bottom: none;
        }

        .meta-label {
            color: #64748b;
        }

        .meta-val {
            color: #0f172a;
        }
    }
}

.preset-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;

    button {
        background: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 8px 6px;
        text-align: left;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        transition: all 0.15s ease;

        b {
            font-size: 12px;
            color: #1e293b;
        }

        span {
            font-size: 10px;
            color: #64748b;
            margin-top: 2px;
        }

        &:hover {
            background: #e2e8f0;
        }

        &.active {
            background: #0369a1;
            border-color: #0369a1;
            color: #ffffff;

            b, span {
                color: #ffffff;
            }
        }
    }
}

.slider-row {
    display: flex;
    align-items: center;
    gap: 10px;

    input[type="range"] {
        flex: 1;
        accent-color: #0284c7;
    }

    .slider-val {
        font-size: 12px;
        font-weight: 700;
        color: #0369a1;
        min-width: 44px;
        text-align: right;
    }
}

.tuner-row {
    display: flex;
    gap: 10px;

    .tuner-col {
        flex: 1;
    }
}

.tuner-select {
    font-size: 12px;
    height: 32px;
}

.vanish-section {
    background: #f0fdf4;
    border: 1px dashed #86efac;
    border-radius: 8px;
    padding: 8px 10px;

    .vanish-badge {
        background: #166534;
        color: #ffffff;
        font-size: 9px;
        font-weight: 700;
        padding: 1px 5px;
        border-radius: 4px;
        text-transform: uppercase;
    }

    .vanish-help {
        font-size: 10px;
        color: #15803d;
        line-height: 1.2;
    }
}

.tuner-footer {
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    gap: 6px;

    button {
        flex: 1;
        font-size: 11px;
        font-weight: 600;
        padding: 4px 6px;
    }
}
</style>
